import { User } from '@solid/shared';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useContext } from 'react';
import { Yup } from '@solid/shared/services/validation.service';
import { AuthContext } from '../../contexts/auth.context';
import * as authService from '../../services/auth.service';
import styles from '../common/Forms.module.scss';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const createLoginSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string().email(t('validations.email')).required(t('validations.emailRequired')),
    password: Yup.string().required(t('validations.passwordRequired')),
    keepLoggedIn: Yup.boolean(),
  });

const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const { refetchAuthState } = useContext(AuthContext);
  const LoginSchema = createLoginSchema(t);
  const {
    mutate: login,
    error,
    isLoading,
  } = useMutation<User, Error, { email: string; password: string; keepLoggedIn: boolean }>(
    ({ email, password, keepLoggedIn }: { email: string; password: string; keepLoggedIn: boolean }) =>
      authService.loginUser(email, password, keepLoggedIn),
    {
      // We are refetching the user auth state -> this automatically redirects them to the home screen
      onSuccess: () => refetchAuthState(),
    }
  );

  return (
    <Formik
      initialValues={{ email: '', password: '', keepLoggedIn: false }}
      onSubmit={values => login(values)}
      validationSchema={LoginSchema}>
      {({ isValid }) => (
        <Form>
          <div className='form-field'>
            <label htmlFor='login-email'>{t('pages.login.form.email')}</label>
            <div className='control'>
              <Field type='email' name='email' id='login-email' autoComplete='email' required />
            </div>
            <ErrorMessage component='p' className='help is-danger' name='email' />
          </div>

          <div className='form-field'>
            <label htmlFor='login-password'>{t('pages.login.form.password')}</label>
            <div className='control'>
              <Field type='password' name='password' id='login-password' autoComplete='off' required />
            </div>
            <ErrorMessage component='p' className='help is-danger' name='password' />
          </div>

          <div className='form-field'>
            <label className='checkbox' htmlFor='login-keep-me-logged-in'>
              <Field type='checkbox' id='login-keep-me-logged-in' name='keepLoggedIn' />
              <span>{t('pages.login.form.keepLoggedIn')}</span>
            </label>
          </div>

          <div className='form-field'>{error && <div className='notification is-error'>{error.message}</div>}</div>

          <div className='form-field'>
            <button
              className={classNames('btn is-primary', styles.submitBtn, { 'is-loading': isLoading })}
              type='submit'
              disabled={isLoading || !isValid}>
              {t('pages.login.form.button')}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
